import { PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Dropdown,
  Menu,
  Space,
  Typography,
  Upload,
  message,
} from 'antd';
import { AccessControl } from 'components/AccessControl';
import ClientsSelect from 'components/ClientsSelect';
import { RecordInfoDrawer } from 'components/RecordInfoDrawer';
import {
  DELETE_ERROR,
  FETCH_ERROR,
  REQUEST_LOADING,
  SUCCESS_DELETE,
} from 'constants/alerts';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { useDrawers } from 'hooks/useDrawers';
import { useFilters } from 'hooks/useFilters';
import { useHeaderChanger } from 'hooks/useHeaderChanger';
import { usePagination } from 'hooks/usePagination';
import { useQueryParam } from 'hooks/useQueryParam';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NomenclaturesService } from 'services/NomenclaturesService';
import { fetchMeasures } from 'store/reducers/staticHandbooksReducer';
import { ActionsWrapper } from 'ui/ActionsWrapper';
import { downloadDocs } from 'utils/files';
import { priceRender } from 'utils/table';
import { ProductServicesForm } from './ProductServicesForm';
import { ProductServicesTable } from './ProductServicesTable';

const PAGE_TITLE = 'Номенклатура';

function ProductServices() {
  useDocumentTitle(PAGE_TITLE);
  const dispatch = useDispatch();
  const clientId = useQueryParam('client_id');
  const { measuresData } = useSelector((state) => state.staticHandbooks);
  const [productServices, setProductServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const {
    state: { drawerVisible, formIsActive, activeRecord },
    openDrawer,
    closeDrawer,
    openCreateForm,
    openEditForm,
    closeForm,
  } = useDrawers();
  const { setCount } = useHeaderChanger(PAGE_TITLE);
  const {
    page,
    perPage,
    totalRows,
    setPagination,
    setTotalRows,
    resetPagination,
  } = usePagination();
  const { filters, handleFilters, resetFilters } = useFilters({
    client_id: '',
    name: null,
    type: null,
    f_price: undefined,
    t_price: undefined,
  });

  const { client_id: selectedClient } = filters;

  useEffect(() => {
    if (measuresData.items.length === 0) {
      dispatch(fetchMeasures());
    }
  }, []);

  const fetchProductServices = useCallback(async () => {
    if (!page || !perPage) {
      return;
    }

    setLoading(true);

    try {
      const { items, total } = await NomenclaturesService.getProductServices(
        {
          page,
          perPage,
        },
        filters
      );
      setProductServices(items);
      setTotalRows(total);
      setCount(total);
    } catch (err) {
      message.error(FETCH_ERROR);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, filters]);

  useEffect(() => fetchProductServices(), [fetchProductServices]);

  const recordFields = useMemo(() => {
    return [
      { label: 'Наименование', key: 'name' },
      {
        label: 'Тип',
        key: 'type',
        computedValue: activeRecord?.type === 'product' ? 'Товар' : 'Услуга',
      },
      {
        label: 'Цена',
        key: 'rate',
        computedValue: `${priceRender(activeRecord?.rate)} ₸`,
      },
      { label: 'Штрих-код', key: 'barcode' },
      { label: 'Источник', key: 'origin' },
      {
        label: 'Единица',
        key: 'measure_code',
        computedValue: measuresData.items.find(
          ({ code }) => code === activeRecord?.measure_code
        )?.title,
      },
    ];
  }, [activeRecord]);

  const handleDelete = async () => {
    try {
      const isDeleted = await NomenclaturesService.deleteProductService(
        activeRecord?.id
      );

      if (isDeleted) {
        closeDrawer();
        message.success(SUCCESS_DELETE);
        fetchProductServices();
      }
    } catch (err) {
      message.error(DELETE_ERROR);
    }
  };

  const handleClientSelect = (id = '') => {
    resetFilters();
    handleFilters({ client_id: id });
    resetPagination();
  };

  const uploadNomeclatures = async (data) => {
    const exportFile = data?.file;

    if (!exportFile) {
      message.warning('Загрузите excel файл');
      return;
    }

    const formData = new FormData();
    formData.append('file', exportFile);

    const hide = message.loading(REQUEST_LOADING);

    try {
      const isUploaded = await NomenclaturesService.uploadExcelFile(
        clientId,
        formData
      );

      if (isUploaded) {
        message.success('Импорт номерклатуры завершён успешно');
        fetchProductServices();
      }
    } catch (err) {
      message.error('Не удалось загрузить импорт номеклатуры');
    } finally {
      hide();
    }
  };

  const handleDownloadExel = async () => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      await downloadDocs(
        'Шаблон',
        `media/product-service/template`,
        'arraybuffer',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;'
      );
    } catch (error) {
      message.error('Не удалось скачать Exel файл');
    } finally {
      hide();
    }
  };

  const extraActions = (
    <Menu>
      <Menu.Item key="add-doc">
        <Upload
          name="file"
          maxCount={1}
          accept=".xlsx"
          beforeUpload={() => false}
          onChange={uploadNomeclatures}
          showUploadList={false}
        >
          <Typography.Text>Импорт номенклатуры</Typography.Text>
        </Upload>
      </Menu.Item>
      <Menu.Item onClick={handleDownloadExel}>
        <Typography.Text>Скачать шаблон</Typography.Text>
      </Menu.Item>
    </Menu>
  );

  return (
    <>
      <ActionsWrapper>
        <ClientsSelect
          pageTitle={PAGE_TITLE}
          clientId={clientId}
          onSelect={handleClientSelect}
          onClear={handleClientSelect}
        />

        <AccessControl pageId="productServices" action="create">
          <Space align="center" size="small">
            <Button
              disabled={!selectedClient}
              type="primary"
              icon={<PlusOutlined />}
              onClick={openCreateForm}
            >
              Создать
            </Button>

            <Dropdown
              disabled={!selectedClient}
              overlay={extraActions}
              trigger={['click']}
            >
              <Button type="secondary">Еще</Button>
            </Dropdown>
          </Space>
        </AccessControl>
      </ActionsWrapper>

      <ProductServicesTable
        items={productServices}
        loading={loading}
        current={page}
        pageSize={perPage}
        filters={filters}
        total={totalRows}
        onRowClick={openDrawer}
        onChange={setPagination}
        onFilter={(value) => {
          handleFilters(value);
          resetPagination();
        }}
      />

      <RecordInfoDrawer
        record={activeRecord}
        drawerTitle={activeRecord?.name}
        recordFields={recordFields}
        isVisible={drawerVisible}
        onClose={closeDrawer}
        onEdit={openEditForm}
        onDelete={handleDelete}
        deleteConfirmText="Вы точно хотите удалить эту номенклатуру ?"
        pageId="productServices"
      />

      <ProductServicesForm
        isActive={formIsActive}
        initialValues={activeRecord}
        measures={measuresData.items}
        clientId={selectedClient}
        onSuccess={() => fetchProductServices()}
        onClose={closeForm}
      />
    </>
  );
}

export { ProductServices };
